// utils/kursiPlay.ts
// =============================================================================
// EXIT-LEVEL: Kursi Play — Shared utilities, constants, types & helpers
// Used by: create.tsx, home.tsx, lesson player, score engine, dashboard
// =============================================================================
import { Platform } from 'react-native';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { VisualTheme } from '@/constants/kursiPlayThemes';

// ── Types ─────────────────────────────────────────────────────────────────────

export type CourseStatus = 'draft' | 'generating' | 'published' | 'archived';
export type CourseCreationMode = 'text' | 'scratch' | 'video' | 'import';
export type ExerciseType =
  | 'multiple_choice'
  | 'true_false'
  | 'fill_blank'
  | 'open_answer';
export type LessonStatus = 'completed' | 'current' | 'locked';
export type Language = 'he' | 'en' | 'ar';

export interface CourseRow {
  id: string;
  creator_id: string;
  name: string;
  slug: string;
  description: string | null;
  language: Language;
  difficulty: number;
  target_audience: string | null;
  creation_mode: CourseCreationMode;
  primary_color: string;
  secondary_color: string | null;
  world_name: string | null;
  narrative_intro: string | null;
  visual_theme: VisualTheme;
  character_name: string | null;
  character_role: string | null;
  image_prompt: string | null;
  mechanic_config:
    | Array<{
        unit_order: number;
        mechanic:
          | 'graph'
          | 'drag_drop'
          | 'simulator'
          | 'dialogue'
          | 'builder'
          | 'timeline';
        mechanic_description: string;
      }>
    | null;
  pipeline_version: string | null;
  generation_stats: Record<string, unknown> | null;
  logo_url: string | null;
  cover_image_url: string | null;
  tags: string[];
  status: CourseStatus;
  published_at: string | null;
  archived_at: string | null;
  total_lessons: number;
  active_students_count: number;
  total_completions: number;
  avg_rating: number | null;
  total_ratings: number;
  created_at: string;
  updated_at: string;
}

export interface UnitRow {
  id: string;
  course_id: string;
  title: string;
  description: string | null;
  order_index: number;
  created_at: string;
}

export interface LessonRow {
  id: string;
  unit_id: string;
  title: string;
  estimated_minutes: number;
  order_index: number;
  unlock_threshold: number;
  created_at: string;
}

export interface ExerciseRow {
  id: string;
  lesson_id: string;
  type: ExerciseType;
  difficulty: number;
  xp_value: number;
  order_index: number;
  data_json: ExerciseData;
  created_at: string;
}

export interface ExerciseData {
  question?: string;
  options?: string[];
  correct_option?: string;
  answer?: string;
  expected_answer?: string;
  ai_logic_hint?: string;
  impact_factor?: string;
  context_type?: 'theory' | 'simulation' | 'case_study';
  key_takeaway?: string;
}

export interface UserProgressRow {
  id?: string;
  user_id: string;
  course_id: string;
  current_lesson_id: string | null;
  current_unit_id: string | null;
  completion_percentage: number;
  mastery_json: Record<string, number>;
  first_started_at: string;
  last_activity_at: string;
  completed_at: string | null;
}

export interface UserXpRow {
  user_id: string;
  total_xp: number;
  level: number;
  updated_at: string;
}

export interface UserStreakRow {
  user_id: string;
  current_streak_days: number;
  longest_streak_days: number;
  last_activity_date: string | null;
}

export interface GenerationJobRow {
  id: string;
  course_id: string;
  user_id: string;
  status: 'running' | 'done' | 'failed';
  progress: number;
  error: string | null;
  units_created: number | null;
  lessons_created: number | null;
  started_at: string;
  finished_at: string | null;
}

// ── Label dictionaries ────────────────────────────────────────────────────────

export const DIFFICULTY_LABELS: Record<number, string> = {
  1: 'קל מאוד',
  2: 'קל',
  3: 'בינוני',
  4: 'קשה',
  5: 'מאסטר 💀',
};

export const DIFFICULTY_COLORS: Record<number, string> = {
  1: '#10B981',
  2: '#10B981',
  3: '#F59E0B',
  4: '#F97316',
  5: '#EF4444',
};

export const LANGUAGE_LABELS: Record<Language, string> = {
  he: 'עברית',
  en: 'English',
  ar: 'عربية',
};

export const LANGUAGE_FLAGS: Record<Language, string> = {
  he: '🇮🇱',
  en: '🇺🇸',
  ar: '🇸🇦',
};

export const STATUS_CONFIG: Record<
  CourseStatus,
  { label: string; color: string; bg: string; emoji: string }
> = {
  draft: { label: 'טיוטה', color: '#F59E0B', bg: '#F59E0B18', emoji: '📝' },
  generating: {
    label: 'נוצר...',
    color: '#6366F1',
    bg: '#6366F118',
    emoji: '🤖',
  },
  published: { label: 'פעיל', color: '#10B981', bg: '#10B98118', emoji: '✅' },
  archived: { label: 'ארכיון', color: '#6B7280', bg: '#6B728018', emoji: '📦' },
};

export const EXERCISE_TYPE_LABELS: Record<ExerciseType, string> = {
  multiple_choice: 'רב-ברירה',
  true_false: 'נכון / לא נכון',
  fill_blank: 'השלם חסר',
  open_answer: 'תשובה פתוחה',
};

export const EXERCISE_TYPE_ICONS: Record<ExerciseType, string> = {
  multiple_choice: '🔘',
  true_false: '✅',
  fill_blank: '✏️',
  open_answer: '💬',
};

export const CONTEXT_TYPE_CONFIG = {
  theory: { label: 'תיאוריה', emoji: '📖', color: '#475569' },
  simulation: { label: 'סימולציה', emoji: '🎮', color: '#7C3AED' },
  case_study: { label: 'מקרה בוחן', emoji: '🔍', color: '#0891B2' },
} as const;

// ── Selector data ─────────────────────────────────────────────────────────────

export const LANGUAGES: { value: Language; label: string; flag: string }[] = [
  { value: 'he', label: 'עברית', flag: '🇮🇱' },
  { value: 'en', label: 'English', flag: '🇺🇸' },
  { value: 'ar', label: 'عربية', flag: '🇸🇦' },
];

export const DIFFICULTIES: { value: number; label: string; color: string }[] = [
  { value: 1, label: 'קל מאוד', color: '#10B981' },
  { value: 2, label: 'קל', color: '#10B981' },
  { value: 3, label: 'בינוני', color: '#F59E0B' },
  { value: 4, label: 'קשה', color: '#F97316' },
  { value: 5, label: 'מאסטר', color: '#EF4444' },
];

export const CREATION_MODES: {
  id: CourseCreationMode;
  icon: string;
  title: string;
  subtitle: string;
  description: string;
  color: string;
  accentBg: string;
}[] = [
  {
    id: 'text',
    icon: '📄',
    title: 'מתוכן כתוב',
    subtitle: 'PDF / Word / TXT',
    description: 'העלה חומר לימודי קיים — AI בונה קורס מלא תוך דקות',
    color: '#9333EA',
    accentBg: 'rgba(147,51,234,0.10)',
  },
  {
    id: 'scratch',
    icon: '🎮',
    title: 'מאפס',
    subtitle: 'AI יוצר הכל',
    description: 'תאר את הנושא בכמה משפטים ו-AI יבנה קורס שלם',
    color: '#7C3AED',
    accentBg: 'rgba(124,58,237,0.10)',
  },
  {
    id: 'video',
    icon: '🎥',
    title: 'קורס וידאו',
    subtitle: 'תמלול / הרצאה',
    description: 'הדבק תמלול של הרצאה — AI יחלק לשיעורים ויצור שאלות',
    color: '#DB2777',
    accentBg: 'rgba(219,39,119,0.10)',
  },
  {
    id: 'import',
    icon: '📦',
    title: 'ייבוא מסודר',
    subtitle: 'שמור על המבנה',
    description: 'יש לך מבנה קיים? נשמור אותו ונוסיף שכבת משחק בלבד',
    color: '#0891B2',
    accentBg: 'rgba(8,145,178,0.10)',
  },
];

export const CREATION_MODE_LABELS: Record<CourseCreationMode, string> = {
  text: 'מתוכן כתוב',
  scratch: 'מאפס',
  video: 'קורס וידאו',
  import: 'ייבוא מסודר',
};

// ── XP / Level system ─────────────────────────────────────────────────────────

export const LEVEL_MILESTONES = [
  { level: 1, minXp: 0, label: 'מתחיל 🌱' },
  { level: 2, minXp: 120, label: 'צועד קדימה 🚶' },
  { level: 3, minXp: 300, label: 'לומד עקבי 📚' },
  { level: 4, minXp: 560, label: 'מתקדם ⚡' },
  { level: 5, minXp: 900, label: 'מיומן 🎯' },
  { level: 6, minXp: 1400, label: 'מומחה 🏆' },
  { level: 7, minXp: 2100, label: 'אלוף 👑' },
] as const;

export function calcLevel(totalXp: number): number {
  const xp = Math.max(0, totalXp);
  let level = 1;
  for (const milestone of LEVEL_MILESTONES) {
    if (xp >= milestone.minXp) level = milestone.level;
  }
  return level;
}

export function xpForLevel(level: number): number {
  return (
    LEVEL_MILESTONES.find((m) => m.level === Math.max(1, level))?.minXp ??
    LEVEL_MILESTONES[LEVEL_MILESTONES.length - 1].minXp
  );
}

export function xpForNextLevel(level: number): number {
  const current = Math.max(1, level);
  const next = LEVEL_MILESTONES.find((m) => m.level === current + 1);
  return next?.minXp ?? xpForLevel(current) + 500;
}

export function levelProgress(totalXp: number): {
  level: number;
  progress: number;
  xpIntoLevel: number;
  xpToNextLevel: number;
} {
  const level = calcLevel(totalXp);
  const currentFloor = xpForLevel(level);
  const nextFloor = xpForNextLevel(level);
  const xpIntoLevel = totalXp - currentFloor;
  const xpToNextLevel = nextFloor - currentFloor;
  const progress =
    xpToNextLevel > 0 ? Math.round((xpIntoLevel / xpToNextLevel) * 100) : 100;
  return { level, progress, xpIntoLevel, xpToNextLevel };
}

export function levelLabel(level: number): string {
  return (
    LEVEL_MILESTONES.find((m) => m.level === level)?.label ??
    LEVEL_MILESTONES[LEVEL_MILESTONES.length - 1].label
  );
}

export const XP_PER_EXERCISE: Record<number, { min: number; max: number }> = {
  1: { min: 5, max: 15 },
  2: { min: 10, max: 20 },
  3: { min: 20, max: 30 },
  4: { min: 30, max: 50 },
  5: { min: 50, max: 100 },
};

// ── Slug generator ────────────────────────────────────────────────────────────

export function slugify(name: string): string {
  const base = name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\u0590-\u05FF\-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  const suffix = Math.random().toString(36).slice(2, 7);
  return `${base}-${suffix}`;
}

// ── Creator ID resolution ─────────────────────────────────────────────────────

const CREATOR_ID_KEY = '__kp_creator_id__';

function generateAnonymousCreatorId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const seed = `${Date.now()}_${Math.random()}`;
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  const hex = `${hash.toString(16)}${Math.floor(Math.random() * 0xffffffff).toString(16)}`.padEnd(16, '0');
  return `00000000-0000-4000-8000-${hex.slice(0, 12)}`;
}

export function getCreatorId(sessionUserId?: string): string {
  if (sessionUserId) return sessionUserId;
  if (Platform.OS !== 'web') return generateAnonymousCreatorId();
  try {
    let id = localStorage.getItem(CREATOR_ID_KEY);
    if (!id) {
      id = generateAnonymousCreatorId();
      localStorage.setItem(CREATOR_ID_KEY, id);
    }
    return id;
  } catch {
    return generateAnonymousCreatorId();
  }
}

export async function resolveCreatorId(
  supabase: SupabaseClient,
): Promise<string> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return getCreatorId(session?.user?.id);
  } catch {
    return getCreatorId();
  }
}

// ── Course validation ─────────────────────────────────────────────────────────

export interface CourseFormValues {
  name: string;
  description: string;
  language: Language;
  difficulty: number;
  targetAudience: string;
  primaryColor: string;
  creationMode: CourseCreationMode;
}

export interface CourseFormErrors {
  name?: string;
  description?: string;
  targetAudience?: string;
}

export function validateCourseForm(
  values: CourseFormValues,
  fileProvided: boolean,
  transcriptLength: number,
): CourseFormErrors {
  const errors: CourseFormErrors = {};

  if (!values.name.trim()) errors.name = 'שם הקורס נדרש';
  else if (values.name.trim().length < 2)
    errors.name = 'שם חייב להכיל לפחות 2 תווים';
  else if (values.name.trim().length > 120)
    errors.name = 'שם ארוך מדי (מקסימום 120)';

  if (values.creationMode === 'scratch' && !values.description.trim()) {
    errors.description = 'נדרש תיאור נושא ל"מאפס"';
  }

  return errors;
}

export function isCourseFormValid(
  values: CourseFormValues,
  fileProvided: boolean,
  transcriptLength: number,
): boolean {
  const errors = validateCourseForm(values, fileProvided, transcriptLength);
  if (Object.keys(errors).length > 0) return false;
  if (
    (values.creationMode === 'text' || values.creationMode === 'import') &&
    !fileProvided
  )
    return false;
  if (values.creationMode === 'video' && transcriptLength < 50) return false;
  return true;
}

// ── Color palette ─────────────────────────────────────────────────────────────

export const COURSE_PALETTE = [
  '#9333EA',
  '#7C3AED',
  '#2563EB',
  '#0891B2',
  '#059669',
  '#D97706',
  '#DC2626',
  '#DB2777',
];

export function isValidHexColor(hex: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(hex);
}

export function contrastColor(hex: string): '#ffffff' | '#000000' {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '#ffffff';
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  // Relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

// ── Share URL builder ─────────────────────────────────────────────────────────

export function buildShareUrl(slug: string): string {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return `${window.location.origin}/learn/path/${slug}`;
  }
  return `/learn/path/${slug}`;
}

export async function copyToClipboard(text: string): Promise<boolean> {
  if (Platform.OS !== 'web' || typeof navigator === 'undefined') return false;
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback for older browsers
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    return true;
  } catch {
    return false;
  }
}

// ── Time formatting ───────────────────────────────────────────────────────────

export function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} דק׳`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}ש׳ ${m}ד׳` : `${h} שעות`;
}

export function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function formatRelativeDate(iso: string): string {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const min = Math.floor(diff / 60_000);
    const hr = Math.floor(diff / 3_600_000);
    const day = Math.floor(diff / 86_400_000);
    if (min < 2) return 'עכשיו';
    if (min < 60) return `לפני ${min} דקות`;
    if (hr < 24) return `לפני ${hr} שעות`;
    if (day < 7) return `לפני ${day} ימים`;
    return formatDate(iso);
  } catch {
    return iso;
  }
}

// ── Number formatting ─────────────────────────────────────────────────────────

export function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString('he-IL');
}

export function formatXp(xp: number): string {
  if (xp >= 10_000) return `${(xp / 1000).toFixed(0)}K XP`;
  return `${xp.toLocaleString()} XP`;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// ── Completion helpers ────────────────────────────────────────────────────────

export function completionGrade(pct: number): {
  label: string;
  color: string;
  emoji: string;
} {
  if (pct >= 90) return { label: 'מצוין', color: '#10B981', emoji: '🏆' };
  if (pct >= 70) return { label: 'טוב', color: '#10B981', emoji: '🎯' };
  if (pct >= 50) return { label: 'סביר', color: '#F59E0B', emoji: '📈' };
  return { label: 'צריך תרגול', color: '#EF4444', emoji: '💪' };
}

export function estimatedReadingTime(text: string): number {
  // Average Hebrew reading speed: ~180 words/min
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 180));
}

// ── Guest UID utilities ───────────────────────────────────────────────────────

export function isGuestUid(uid: string): boolean {
  return uid.startsWith('guest_');
}

export function isValidGuestUid(uid: string): boolean {
  return (
    uid.startsWith('guest_') &&
    uid.length >= 8 &&
    uid.length <= 200 &&
    /^guest_[a-zA-Z0-9_\-]+$/.test(uid)
  );
}

export function generateGuestUid(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `guest_${ts}_${rand}`;
}

export type StreakState = {
  days: number;
  lastActivityDate: string | null;
  atRisk: boolean;
  hoursLeftToday: number;
  canUseFreeze: boolean;
};

export function getStreakState(
  days: number,
  lastActivityDate: string | null,
): StreakState {
  const now = new Date();
  const today = now.toISOString().slice(0, 10);
  const last = lastActivityDate?.slice(0, 10) ?? null;
  const endOfDay = new Date(now);
  endOfDay.setHours(23, 59, 59, 999);
  const hoursLeftToday = Math.max(
    0,
    Math.round((endOfDay.getTime() - now.getTime()) / 3_600_000),
  );

  const atRisk = !!last && last !== today;
  return {
    days: Math.max(0, days || 0),
    lastActivityDate: lastActivityDate ?? null,
    atRisk,
    hoursLeftToday,
    canUseFreeze: false,
  };
}

// ── API helpers ───────────────────────────────────────────────────────────────

export async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  delayMs = 500,
): Promise<T> {
  let lastErr: Error | null = null;
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (e: any) {
      lastErr = e;
      if (i < maxRetries - 1) {
        await new Promise((r) => setTimeout(r, delayMs * Math.pow(2, i)));
      }
    }
  }
  throw lastErr;
}

export function buildApiHeaders(accessToken?: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  return headers;
}

// ── Local storage helpers ─────────────────────────────────────────────────────

export function localGet(key: string): string | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  try {
    return window.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function localSet(key: string, value: string): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  try {
    window.localStorage?.setItem(key, value);
  } catch {}
}

export function localRemove(key: string): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  try {
    window.localStorage?.removeItem(key);
  } catch {}
}

export function localGetJson<T>(key: string): T | null {
  const raw = localGet(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function localSetJson(key: string, value: unknown): void {
  try {
    localSet(key, JSON.stringify(value));
  } catch {}
}

// ── Accessibility ─────────────────────────────────────────────────────────────

export const RTL_STYLE = {
  writingDirection: 'rtl' as const,
  textAlign: 'right' as const,
};
export const LTR_STYLE = {
  writingDirection: 'ltr' as const,
  textAlign: 'left' as const,
};

export function dirStyle(lang: Language) {
  return lang === 'he' || lang === 'ar' ? RTL_STYLE : LTR_STYLE;
}

// ── Course analytics helpers ──────────────────────────────────────────────────

export interface CourseStats {
  total_students: number;
  total_completions: number;
  completion_rate: number;
  avg_rating: number | null;
  total_ratings: number;
  avg_completion: number;
}

export function calcCompletionRate(
  students: number,
  completions: number,
): number {
  if (students <= 0) return 0;
  return Math.round((completions / students) * 100);
}

export function getCreatorTierLabel(courses: number, students: number): string {
  if (students >= 10_000 || courses >= 20) return '🌟 Elite Creator';
  if (students >= 1_000 || courses >= 10) return '🚀 Pro Creator';
  if (students >= 100 || courses >= 3) return '⚡ Active Creator';
  return '🌱 New Creator';
}

// ── Pagination ────────────────────────────────────────────────────────────────

export function paginationRange(
  page: number,
  totalPages: number,
  delta = 2,
): (number | '...')[] {
  const range: (number | '...')[] = [];
  const left = Math.max(1, page - delta);
  const right = Math.min(totalPages, page + delta);

  if (left > 1) {
    range.push(1);
    if (left > 2) range.push('...');
  }
  for (let i = left; i <= right; i++) range.push(i);
  if (right < totalPages) {
    if (right < totalPages - 1) range.push('...');
    range.push(totalPages);
  }

  return range;
}

// =============================================================================
// SECTION 2 — Extended utilities: hooks, analytics, review queue, i18n
// =============================================================================

// ── useAsync hook ─────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';

export function useAsync<T>(
  fn: () => Promise<T>,
  deps: unknown[] = [],
): {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fn();
      setData(result);
    } catch (e: any) {
      setError(e?.message ?? 'שגיאה');
    } finally {
      setLoading(false);
    }
  }, deps);

  useEffect(() => {
    run();
  }, [run]);
  return { data, loading, error, reload: run };
}

// ── useDebounce hook ──────────────────────────────────────────────────────────

export function useDebounce<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// ── useInterval hook ──────────────────────────────────────────────────────────

export function useInterval(fn: () => void, ms: number | null) {
  const fnRef = useRef(fn);
  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);
  useEffect(() => {
    if (ms === null) return;
    const id = setInterval(() => fnRef.current(), ms);
    return () => clearInterval(id);
  }, [ms]);
}

// ── usePrevious hook ──────────────────────────────────────────────────────────

export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

// ── useOnMount hook ───────────────────────────────────────────────────────────

export function useOnMount(fn: () => void | (() => void)) {
  useEffect(() => {
    return fn() ?? undefined;
  }, []);
}

// ── Supabase helpers ──────────────────────────────────────────────────────────

export async function getAuthSession(
  supabase: any,
): Promise<{ userId: string | null; token: string | null }> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return {
      userId: session?.user?.id ?? null,
      token: session?.access_token ?? null,
    };
  } catch {
    return { userId: null, token: null };
  }
}

export async function signInAnonymously(supabase: any): Promise<string | null> {
  try {
    const { data, error } = await supabase.auth.signInAnonymously();
    if (error || !data.user) return null;
    return data.user.id;
  } catch {
    return null;
  }
}


export async function ensureKursiPlayOpenAccessSession(
  supabase: any,
): Promise<string | null> {
  const { userId } = await getAuthSession(supabase);
  if (userId) return userId;
  return signInAnonymously(supabase);
}

// ── Review queue helpers ──────────────────────────────────────────────────────

export interface ReviewItem {
  exercise_id: string;
  priority_score: number;
  review_count: number;
  next_review_at: string;
}

export function sortReviewQueue(items: ReviewItem[]): ReviewItem[] {
  const now = new Date();
  return [...items]
    .filter((item) => new Date(item.next_review_at) <= now)
    .sort((a, b) => b.priority_score - a.priority_score);
}

export function getReviewSessionSize(totalDue: number): number {
  if (totalDue <= 5) return totalDue;
  if (totalDue <= 20) return 10;
  return 15;
}

// ── Analytics event helpers ───────────────────────────────────────────────────

export type AnalyticsEvent =
  | { event: 'lesson_started'; lesson_id: string; course_id: string }
  | {
      event: 'lesson_completed';
      lesson_id: string;
      course_id: string;
      score: number;
    }
  | {
      event: 'exercise_answered';
      exercise_id: string;
      is_correct: boolean;
      xp: number;
    }
  | { event: 'course_completed'; course_id: string; total_xp: number }
  | { event: 'streak_extended'; streak_days: number }
  | { event: 'level_up'; new_level: number; total_xp: number };

export function logEvent(event: AnalyticsEvent) {
  // Safe no-op logger — swap with your analytics provider
  if (process.env.NODE_ENV === 'development') {
    console.log('[KP Analytics]', JSON.stringify(event));
  }
}

// ── i18n helpers ──────────────────────────────────────────────────────────────

export type SupportedLocale = 'he' | 'en' | 'ar';

export const UI_STRINGS: Record<SupportedLocale, Record<string, string>> = {
  he: {
    start_learning: 'התחל ללמוד',
    continue: 'המשך',
    lesson_complete: 'שיעור הושלם!',
    course_complete: 'קורס הושלם!',
    locked: 'נעול',
    xp_earned: 'XP הרווחת',
    new_achievement: 'הישג חדש!',
    streak_days: 'ימי רצף',
    level: 'רמה',
    try_again: 'נסה שוב',
    next_lesson: 'שיעור הבא',
    back: 'חזרה',
    publish: 'פרסם',
    share: 'שתף',
    delete: 'מחק',
    save: 'שמור',
    cancel: 'ביטול',
    loading: 'טוען...',
    error: 'שגיאה',
    retry: 'נסה שוב',
    correct: 'נכון!',
    incorrect: 'לא נכון',
    show_answer: 'הצג תשובה',
    skip: 'דלג',
  },
  en: {
    start_learning: 'Start Learning',
    continue: 'Continue',
    lesson_complete: 'Lesson Complete!',
    course_complete: 'Course Complete!',
    locked: 'Locked',
    xp_earned: 'XP Earned',
    new_achievement: 'New Achievement!',
    streak_days: 'Day Streak',
    level: 'Level',
    try_again: 'Try Again',
    next_lesson: 'Next Lesson',
    back: 'Back',
    publish: 'Publish',
    share: 'Share',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    loading: 'Loading...',
    error: 'Error',
    retry: 'Retry',
    correct: 'Correct!',
    incorrect: 'Incorrect',
    show_answer: 'Show Answer',
    skip: 'Skip',
  },
  ar: {
    start_learning: 'ابدأ التعلم',
    continue: 'استمر',
    lesson_complete: 'اكتمل الدرس!',
    course_complete: 'اكتمل الكورس!',
    locked: 'مقفل',
    xp_earned: 'XP مكتسب',
    new_achievement: 'إنجاز جديد!',
    streak_days: 'أيام متتالية',
    level: 'مستوى',
    try_again: 'حاول مجدداً',
    next_lesson: 'الدرس التالي',
    back: 'رجوع',
    publish: 'نشر',
    share: 'مشاركة',
    delete: 'حذف',
    save: 'حفظ',
    cancel: 'إلغاء',
    loading: 'جار التحميل...',
    error: 'خطأ',
    retry: 'أعد المحاولة',
    correct: 'صحيح!',
    incorrect: 'خطأ',
    show_answer: 'أظهر الإجابة',
    skip: 'تخطي',
  },
};

export function t(key: string, locale: SupportedLocale = 'he'): string {
  return UI_STRINGS[locale]?.[key] ?? UI_STRINGS['he']?.[key] ?? key;
}

// ── Device & platform helpers ─────────────────────────────────────────────────

export function isWeb(): boolean {
  return Platform.OS === 'web';
}

export function isMobileWeb(): boolean {
  if (!isWeb() || typeof navigator === 'undefined') return false;
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
}

export function supportsHaptics(): boolean {
  return Platform.OS === 'ios' || Platform.OS === 'android';
}

// ── Error helpers ─────────────────────────────────────────────────────────────

export function isSupabaseError(e: unknown): boolean {
  return typeof e === 'object' && e !== null && 'code' in e && 'message' in e;
}

export function getErrorMessage(e: unknown): string {
  if (typeof e === 'string') return e;
  if (e instanceof Error) return e.message;
  if (isSupabaseError(e)) return (e as any).message ?? 'שגיאת Supabase';
  return 'שגיאה לא ידועה';
}

export function isNetworkError(e: unknown): boolean {
  const msg = getErrorMessage(e).toLowerCase();
  return (
    msg.includes('network') ||
    msg.includes('fetch') ||
    msg.includes('timeout') ||
    msg.includes('offline')
  );
}

export function isAuthError(e: unknown): boolean {
  const msg = getErrorMessage(e).toLowerCase();
  return (
    msg.includes('auth') ||
    msg.includes('unauthorized') ||
    msg.includes('token') ||
    msg.includes('403') ||
    msg.includes('401')
  );
}

// ── XP reward calculator ──────────────────────────────────────────────────────

export interface XpReward {
  base: number;
  diff_bonus: number;
  score_mult: number;
  streak_bonus: number;
  total: number;
  breakdown: string;
}

export function calcXpReward(opts: {
  baseXp: number;
  difficulty: number;
  score: number;
  streak: number;
}): XpReward {
  const { baseXp, difficulty, score, streak } = opts;

  const diffMultiplier =
    [0, 1.0, 1.1, 1.2, 1.4, 1.8][Math.max(1, Math.min(5, difficulty))] ?? 1.0;
  const scoreMultiplier =
    score >= 90 ? 1.15 : score >= 70 ? 1.0 : score >= 40 ? 0.35 : 0;
  const diffXp = Math.round(baseXp * diffMultiplier);
  const afterScore = Math.round(diffXp * scoreMultiplier);
  const streakBonus = streak > 3 ? Math.min(25, (streak - 3) * 5) : 0;
  const total = afterScore + streakBonus;

  const parts: string[] = [`בסיס ${baseXp}`];
  if (diffMultiplier > 1) parts.push(`×${diffMultiplier} קושי`);
  if (scoreMultiplier !== 1 && scoreMultiplier > 0)
    parts.push(`×${scoreMultiplier} ניקוד`);
  if (streakBonus > 0) parts.push(`+${streakBonus} 🔥`);

  return {
    base: baseXp,
    diff_bonus: diffXp,
    score_mult: afterScore,
    streak_bonus: streakBonus,
    total,
    breakdown: parts.join(' → ') + ` = ${total} XP`,
  };
}

// ── Leaderboard helpers ───────────────────────────────────────────────────────

export interface LeaderboardEntry {
  rank: number;
  user_id: string;
  display_name: string;
  total_xp: number;
  level: number;
  streak: number;
  completed: boolean;
}

export function rankEntries(
  entries: Omit<LeaderboardEntry, 'rank'>[],
): LeaderboardEntry[] {
  return [...entries]
    .sort((a, b) => b.total_xp - a.total_xp)
    .map((e, i) => ({ ...e, rank: i + 1 }));
}

export function getRankEmoji(rank: number): string {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `#${rank}`;
}

export function getRankColor(rank: number): string {
  if (rank === 1) return '#F59E0B';
  if (rank === 2) return '#94A3B8';
  if (rank === 3) return '#CD7F32';
  return '#64748B';
}

// ── Session storage helpers ───────────────────────────────────────────────────

export const SESSION_KEYS = {
  guestUid: (slug: string) => `kp_guest_uid:${slug}`,
  lastLesson: (slug: string) => `kp_last_lesson:${slug}`,
  lastUnit: (slug: string) => `kp_last_unit:${slug}`,
  creatorId: '__kp_creator_id__',
} as const;

// ── Environment detection ─────────────────────────────────────────────────────

export const IS_DEV = process.env.NODE_ENV === 'development';
export const IS_PROD = process.env.NODE_ENV === 'production';

// ── Misc pure utilities ───────────────────────────────────────────────────────

export function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export function randomId(prefix = 'id'): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export function pick<T extends object, K extends keyof T>(
  obj: T,
  keys: K[],
): Pick<T, K> {
  return keys.reduce((acc, k) => ({ ...acc, [k]: obj[k] }), {} as Pick<T, K>);
}

export function omit<T extends object, K extends keyof T>(
  obj: T,
  keys: K[],
): Omit<T, K> {
  const result = { ...obj };
  keys.forEach((k) => delete result[k]);
  return result as Omit<T, K>;
}

export function groupBy<T>(
  arr: T[],
  fn: (item: T) => string,
): Record<string, T[]> {
  return arr.reduce(
    (acc, item) => {
      const key = fn(item);
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    },
    {} as Record<string, T[]>,
  );
}

export function uniqueBy<T>(arr: T[], fn: (item: T) => unknown): T[] {
  const seen = new Set();
  return arr.filter((item) => {
    const key = fn(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function chunk<T>(arr: T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size),
  );
}
